<template>
    <div class="rag-color-and-icon">
        <div class="rag-color">
            <div class="icon-slot">
                <promotion-rag-colour
                    :vertical="true"
                    :colour="colour"
                    :make-read-only="makeReadOnly"
                    :read-only-reason="readOnlyReason"
                    :display-count="false"
                    :big-circle="bigCircle"
                />
            </div>
            <div class="icon-slot">
                <icon v-if="showParentChildIcon" medium icon-name="custom:parent-child-icon-red" />
            </div>
            <div class="icon-slot">
                <execution-errors-dialog
                    v-if="hasErrors && showAllThreeIcons"
                    :execution-errors="executionErrors"
                    :forecasting-errors="forecastingErrors"
                    :in-tool-validations="inToolValidations"
                />
            </div>
        </div>
        <div class="icon">
            <div class="icon-slot">
                <promotion-unlock-dialog
                    :icon-name="iconName"
                    :promotion-id="promotionId"
                    :sub-campaign="subCampaign"
                    :child-block-icon="childBlockIcon"
                    class="unlock-dialog-wrapper"
                    @activator-visibility-change="disableWorkflowIcon = $event"
                    @show-unlock-button-change="showUnlockButton = $event"
                />
            </div>
            <div class="icon-slot">
                <promotion-workflow-icon
                    v-if="showWorkflowIcon"
                    :workflow-state="workflowState"
                    :promotion-id="promotionId"
                    :sub-campaign="subCampaign"
                    :disabled="disableWorkflowIcon"
                    @action-completed="$emit('action-completed')"
                />
                <spinner-dynamic v-if="execution.executionId" />
            </div>
            <div class="icon-slot">
                <execution-errors-dialog
                    v-if="hasErrors && !showAllThreeIcons"
                    :execution-errors="executionErrors"
                    :forecasting-errors="forecastingErrors"
                    :in-tool-validations="inToolValidations"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { isEmpty, isNil, get, groupBy } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import featureFlags from '@enums/feature-flags';
import notificationTypes from '@enums/notification-types';

const { showPromotionWorkflowIcon } = featureFlags;

export default {
    props: {
        colour: {
            type: Number,
            required: false,
        },
        iconName: {
            type: String,
            required: true,
        },
        execution: {
            type: Object,
            required: false,
        },
        forecasting: {
            type: Object,
            required: false,
        },
        promotionId: {
            type: String,
            default: undefined,
        },
        subCampaign: {
            type: Object,
            required: false,
        },
        notification: {
            type: Object,
            required: false,
        },
        bigCircle: {
            type: Boolean,
            default: false,
        },
        childBlockIcon: {
            type: Boolean,
            default: false,
        },
        workflowState: {
            type: Array,
            default: () => [],
        },
        isParkingLotOrSubCampaignTab: {
            type: Boolean,
            default: false,
        },
        scenario: {
            type: Object,
            required: false,
            default: null,
        },
    },
    emits: ['action-completed'],
    data() {
        return {
            showPromotionWorkflowIcon,
            disableWorkflowIcon: false,
            showUnlockButton: false,
        };
    },
    computed: {
        ...mapState('clientConfig', ['toggleLogic']),
        ...mapGetters('promotions', ['pendingChanges', 'getPromotionNotifications']),
        showWorkflowIcon() {
            return (
                !this.isParkingLotOrSubCampaignTab &&
                this.scenario?.isFavourite &&
                !this.execution.executionId &&
                this.toggleLogic[this.showPromotionWorkflowIcon]
            );
        },
        showAllThreeIcons() {
            return this.showWorkflowIcon && this.showUnlockButton;
        },
        hasErrors() {
            return (
                !isEmpty(this.executionErrors) ||
                !isEmpty(this.forecastingErrors) ||
                !isEmpty(this.inToolValidations)
            );
        },
        forecastingErrors() {
            return isNil(this.forecasting) ? [] : this.forecasting.errors;
        },
        executionErrors() {
            const executionErrors = [];
            if (!isNil(this.execution)) {
                if (this.execution.errors) {
                    executionErrors.push(...this.execution.errors);
                }
                if (this.execution.deleteError) {
                    executionErrors.push(this.execution.deleteError);
                }
            }
            return executionErrors;
        },
        showParentChildIcon() {
            return (
                this.pendingChanges({ promotionId: this.promotionId }) ||
                (this.notification && this.notification.pendingChanges)
            );
        },
        inToolValidations() {
            const notificationKey = notificationTypes.ruleValidationError;
            const notification = this.getPromotionNotifications({
                promotionId: this.promotionId,
                notificationKey,
            });
            if (notification && notification.length) {
                const ruleValidationTypes = get(notification[0], 'details.ruleValidationTypes', []);
                return groupBy(ruleValidationTypes, 'severity');
            }
            return null;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.rag-color-and-icon {
    flex-direction: column;

    :deep(svg) {
        width: 2rem;
        height: 2rem;
    }
}

.rag-color {
    display: flex;
    align-items: center;
    gap: 0.1rem;
}

.icon {
    margin-top: 0.85rem;
    display: flex;
    align-items: center;
    gap: 0.1rem;
}

.icon-slot {
    flex: 0 0 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
